import React, { useCallback, useEffect, useRef, useState } from "react";
import API from "../config";

const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

const safeJson = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const triggerDownload = (url, filename = "report.csv") => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const triggerBlobDownload = async (res, filename) => {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const downloadCompletedReport = async (reportId) => {
  const res = await authFetch(`${API}/reports/download/${reportId}`);
  const data = await safeJson(res);

  if (!res.ok || !data.downloadUrl) {
    throw new Error(data.error || "Unable to prepare report download.");
  }

  triggerDownload(data.downloadUrl, data.filename);
};

const statusClass = (status) => {
  if (status === "completed") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "failed") return "border-red-200 bg-red-50 text-red-600";
  return "border-amber-200 bg-amber-50 text-amber-700";
};

const Reports = () => {
  const [devices, setDevices] = useState([]);
  const [reports, setReports] = useState([]);
  const [filters, setFilters] = useState({ deviceId: "", startDate: "", endDate: "" });
  const [search, setSearch] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const pollRef = useRef({});
  const startPollingRef = useRef(null);

  const getRangeDays = (start, end) =>
    Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24));

  const startPolling = useCallback((id) => {
    if (pollRef.current[id]) return;

    pollRef.current[id] = setInterval(async () => {
      try {
        const res = await authFetch(`${API}/reports/status/${id}`);
        const data = await safeJson(res);

        if (data.status === "completed") {
          clearInterval(pollRef.current[id]);
          delete pollRef.current[id];
          setReports((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, status: "completed" } : r
            )
          );
          await downloadCompletedReport(id);
        } else if (data.status === "failed") {
          clearInterval(pollRef.current[id]);
          delete pollRef.current[id];
          setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: "failed" } : r)));
        }
      } catch {
        // Keep polling through temporary network issues.
      }
    }, 3000);
  }, []);

  useEffect(() => {
    startPollingRef.current = startPolling;
  }, [startPolling]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/reports/devices`);
      if (!res.ok) return;
      const data = await safeJson(res);
      setDevices(data.devices || data || []);
    } catch {
      setDevices([]);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/reports`);
      if (!res.ok) return;
      const data = await safeJson(res);
      const list = data.reports || [];
      setReports(list);
      list.forEach((r) => {
        if (r.status === "pending" || r.status === "processing") {
          startPollingRef.current?.(r.id);
        }
      });
    } catch {
      // Ignore refresh errors; the existing table stays visible.
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchReports();
  }, [fetchDevices, fetchReports]);

  useEffect(() => {
    const interval = setInterval(fetchReports, 5000);
    return () => {
      clearInterval(interval);
      Object.values(pollRef.current).forEach(clearInterval);
      pollRef.current = {};
    };
  }, [fetchReports]);

  const downloadCSV = async () => {
    const { deviceId, startDate, endDate } = filters;

    if (!deviceId || !startDate || !endDate) {
      alert("Please select a device and both dates.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      alert("End date cannot be before start date.");
      return;
    }

    setLoading(true);
    setStatusMsg("Preparing report...");

    if (getRangeDays(startDate, endDate) <= 3) {
      try {
        const directRes = await authFetch(`${API}/reports/download-direct`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId, startDate, endDate }),
        });

        if (directRes.ok) {
          const contentType = directRes.headers.get("content-type") || "";
          if (contentType.includes("text/csv")) {
            await triggerBlobDownload(directRes, `report_${deviceId}_${startDate}_${endDate}.csv`);
            setStatusMsg("Downloaded.");
            setLoading(false);
            await fetchReports();
            return;
          }

          const directData = await safeJson(directRes);
          if (directData.downloadUrl) {
            triggerDownload(directData.downloadUrl, `report_${deviceId}_${startDate}_${endDate}.csv`);
            setStatusMsg("Downloaded.");
            setLoading(false);
            await fetchReports();
            return;
          }
        } else {
          const directError = await safeJson(directRes);
          if (directRes.status === 404 && directError.error) {
            setStatusMsg(directError.error);
            setLoading(false);
            return;
          }
        }
      } catch {
        // Fall through to Athena path.
      }
    }

    try {
      const res = await authFetch(`${API}/reports/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, startDate, endDate }),
      });

      if (!res.ok) {
        const errData = await safeJson(res);
        setStatusMsg(errData.error || "Failed to create report.");
        setLoading(false);
        return;
      }

      const data = await safeJson(res);
      const newId = data.reportId;
      setReports((prev) => [
        {
          id: newId,
          device_id: deviceId,
          start_date: startDate,
          end_date: endDate,
          status: "pending",
          created_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setStatusMsg("Report queued in Athena. Download will start automatically when ready.");
      startPolling(newId);
    } catch {
      setStatusMsg("Network error. Please try again.");
    }

    setLoading(false);
  };

  const filteredReports = reports.filter((r) => {
    const matchesSearch = !search || r.device_id?.toLowerCase().includes(search.toLowerCase());
    const date = new Date(r.created_at || r.updated_at);
    const matchesStart = !filterStart || date >= new Date(filterStart);
    const matchesEnd = !filterEnd || date <= new Date(`${filterEnd}T23:59:59`);
    return matchesSearch && matchesStart && matchesEnd;
  });

  const totalPages = Math.ceil(filteredReports.length / pageSize) || 1;
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="space-y-6">
      <section className="cb-hero-panel rounded-lg px-6 py-5 text-white">
        <p className="text-xs font-bold uppercase text-emerald-100">Athena Exports</p>
        <div className="mt-1 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Reports</h1>
            <p className="mt-2 max-w-2xl text-sm font-medium text-emerald-50/85">
              Download telemetry CSV files for any device. Long ranges are queued through Athena and download automatically when ready.
            </p>
          </div>
          <div className="rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold">
            {reports.length} saved exports
          </div>
        </div>
      </section>

      <section className="cb-panel rounded-lg p-5">
        <p className="cb-section-title">Generate Export</p>
        <h2 className="mt-1 text-lg font-black text-slate-950">Create telemetry CSV</h2>
        <div className="mt-4 grid gap-3 lg:grid-cols-[1.15fr_0.8fr_0.8fr_auto]">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-slate-500">Device</span>
            <select
              value={filters.deviceId}
              onChange={(e) => setFilters((p) => ({ ...p, deviceId: e.target.value }))}
              className="w-full border px-3 py-2 text-sm"
            >
              <option value="">Select device</option>
              {devices.map((d) => (
                <option key={d.id || d.device_uid} value={d.device_uid}>
                  {d.device_uid}{d.name ? ` - ${d.name}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-slate-500">Start date</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
              className="w-full border px-3 py-2 text-sm"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-slate-500">End date</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
              className="w-full border px-3 py-2 text-sm"
            />
          </label>

          <button
            onClick={downloadCSV}
            disabled={loading}
            className="mt-5 rounded-lg bg-green-600 px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            {loading ? "Processing..." : "Download CSV"}
          </button>
        </div>
        {statusMsg && (
          <p className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
            {statusMsg}
          </p>
        )}
      </section>

      <section className="cb-panel flex flex-wrap gap-3 rounded-lg p-3">
        <input
          placeholder="Search device..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="min-w-[260px] flex-1 border px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={filterStart}
          onChange={(e) => {
            setFilterStart(e.target.value);
            setCurrentPage(1);
          }}
          className="border px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={filterEnd}
          onChange={(e) => {
            setFilterEnd(e.target.value);
            setCurrentPage(1);
          }}
          className="border px-3 py-2 text-sm"
        />
      </section>

      <section className="cb-table-shell">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="p-3 text-left">Device</th>
              <th className="p-3 text-left">Date Range</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Updated</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedReports.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-400">
                  No reports found.
                </td>
              </tr>
            ) : (
              paginatedReports.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="p-3 font-mono text-xs font-bold text-slate-800">{r.device_id}</td>
                  <td className="p-3 text-xs text-slate-500">
                    {r.start_date} to {r.end_date}
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex rounded border px-2.5 py-1 text-xs font-bold ${statusClass(r.status)}`}>
                      {r.status === "pending" || r.status === "processing" ? `Queued - ${r.status}` : r.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-slate-500">
                    {r.updated_at ? new Date(r.updated_at).toLocaleString() : "-"}
                  </td>
                  <td className="p-3">
                    {r.status === "completed" ? (
                      <button
                        type="button"
                        onClick={() =>
                          downloadCompletedReport(r.id).catch((err) => setStatusMsg(err.message))
                        }
                        className="font-bold text-emerald-700 underline"
                      >
                        Download
                      </button>
                    ) : r.status === "failed" ? (
                      <span className="text-xs font-bold text-red-500">Failed</span>
                    ) : (
                      <span className="text-xs font-semibold text-gray-400">Waiting...</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <div className="flex items-center justify-between">
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="border px-3 py-2 text-sm"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="rounded-lg border bg-white px-3 py-2 text-sm font-bold disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-sm font-bold text-slate-500">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="rounded-lg border bg-white px-3 py-2 text-sm font-bold disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
